import { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'
import {
  ArrowLeft,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Trophy,
  RotateCcw,
  ChevronRight,
  ChevronLeft,
  Volume2
} from 'lucide-react'
import AudioPlayer from '@/components/common/AudioPlayer'
import { triggerUnitExamPassed, triggerUnitExamFailed } from '@/lib/flowTrigger'

interface ExamQuestion {
  id: string
  question: string
  audio_url?: string | null
  options: string[]
  correct_answer: string
  points: number
  order_num: number
}

interface UnitExam {
  id: string
  unit_id: string
  title: string
  description: string | null
  passing_score: number
  time_limit: number
  max_attempts: number
  is_active: boolean
}

interface Unit {
  id: string
  title: string
  order_num: number
}

interface ExamAttempt {
  id: string
  student_id: string
  exam_id: string
  score: number
  passed: boolean
  answers: Record<string, string>
  time_taken_seconds: number
  attempt_number: number
  completed_at: string
  created_at: string
}

export default function UnitExamPage() {
  const { unitId } = useParams()
  const { user, profile, student } = useAuth()
  const navigate = useNavigate()

  const [unit, setUnit] = useState<Unit | null>(null)
  const [exam, setExam] = useState<UnitExam | null>(null)
  const [questions, setQuestions] = useState<ExamQuestion[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  // Estado del examen
  const [examStarted, setExamStarted] = useState(false)
  const [examFinished, setExamFinished] = useState(false)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [timeRemaining, setTimeRemaining] = useState(0)
  const [visitedQuestions, setVisitedQuestions] = useState<Set<number>>(new Set([0]))
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false)

  // Resultados
  const [score, setScore] = useState(0)
  const [passed, setPassed] = useState(false)
  const [correctAnswers, setCorrectAnswers] = useState(0)
  const [_isSaving, setIsSaving] = useState(false)

  // Intentos previos
  const [previousAttempts, setPreviousAttempts] = useState<ExamAttempt[]>([])
  const [bestAttempt, setBestAttempt] = useState<ExamAttempt | null>(null)
  const [lessonsIncomplete, setLessonsIncomplete] = useState(false)
  const [completedLessonsCount, setCompletedLessonsCount] = useState(0)
  const [totalLessonsCount, setTotalLessonsCount] = useState(0)

  useEffect(() => {
    if (unitId) {
      loadExamData()
    }
  }, [unitId])

  // Timer
  useEffect(() => {
    if (!examStarted || examFinished || timeRemaining <= 0) return

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer)
          finishExam()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [examStarted, examFinished, timeRemaining])

  const loadExamData = async () => {
    setIsLoading(true)
    try {
      // Cargar unidad
      const { data: unitRaw } = await (supabase
        .from('units') as any)
        .select('*')
        .eq('id', unitId as string)
        .single()

      if (unitRaw) {
        setUnit({
          id: unitRaw.id,
          title: unitRaw.title || unitRaw.name_es || unitRaw.name_fr || '',
          order_num: unitRaw.order_num ?? unitRaw.unit_number ?? 0
        })
      }

      // Verificar que todas las lecciones estén completadas
      if (student?.id) {
        const { data: lessons } = await (supabase
          .from('lessons') as any)
          .select('id')
          .eq('unit_id', unitId as string)

        const totalLessons = lessons?.length || 0
        setTotalLessonsCount(totalLessons)

        if (totalLessons > 0) {
          const lessonIds = lessons.map((l: any) => l.id)
          const { count } = await (supabase
            .from('student_lesson_progress') as any)
            .select('*', { count: 'exact', head: true })
            .eq('student_id', student.id)
            .in('lesson_id', lessonIds)
            .eq('status', 'completed')

          const completed = count || 0
          setCompletedLessonsCount(completed)

          if (completed < totalLessons) {
            setLessonsIncomplete(true)
            setIsLoading(false)
            return
          }
        }
      }

      // Cargar examen
      const { data: examRaw, error: examError } = await (supabase
        .from('unit_exams') as any)
        .select('*')
        .eq('unit_id', unitId as string)
        .maybeSingle()

      if (examError) {
        console.error('Error cargando examen:', examError)
        setError('Error al cargar el examen')
        return
      }

      if (!examRaw) {
        setError('No hay examen disponible para esta unidad')
        return
      }

      // Map bilingual columns
      const examData = {
        ...examRaw,
        title: examRaw.title_es || examRaw.title_fr || examRaw.title || '',
        description: examRaw.description_es || examRaw.description_fr || examRaw.description || null,
        time_limit: examRaw.time_limit_minutes ?? examRaw.time_limit ?? 30
      }

      setExam(examData)

      // Cargar preguntas con opciones
      const { data: questionsRaw, error: questionsError } = await (supabase
        .from('exam_questions') as any)
        .select('*, options:exam_question_options(*)')
        .eq('exam_id', examData.id)
        .order('order_index')

      if (questionsError) {
        console.error('Error cargando preguntas:', questionsError)
        setError('Error al cargar las preguntas')
        return
      }

      if (!questionsRaw || questionsRaw.length === 0) {
        setError('Este examen no tiene preguntas configuradas')
        return
      }

      // Map questions from bilingual + normalized options
      const mappedQuestions = questionsRaw.map((q: any) => {
        const opts = (q.options || []).sort((a: any, b: any) => (a.order_index || 0) - (b.order_index || 0))
        return {
          id: q.id,
          question: q.question_es || q.question_fr || q.question || '',
          audio_url: q.audio_url || null,
          options: opts.length > 0
            ? opts.map((o: any) => o.content_es || o.content_fr || '')
            : q.options || [],
          correct_answer: opts.find((o: any) => o.is_correct)?.content_es
            || opts.find((o: any) => o.is_correct)?.content_fr
            || q.correct_answer || '',
          points: q.points,
          order_num: q.order_index ?? q.order_num ?? 0
        }
      })

      setQuestions(mappedQuestions)

      // Cargar intentos previos del estudiante
      if (student?.id) {
        const { data: attemptsData } = await (supabase
          .from('exam_attempts') as any)
          .select('*')
          .eq('exam_id', examData.id)
          .eq('student_id', student.id)
          .order('created_at', { ascending: false })

        if (attemptsData && attemptsData.length > 0) {
          setPreviousAttempts(attemptsData)
          // Encontrar el mejor intento (mayor puntaje)
          const best = attemptsData.reduce((prev: any, current: any) =>
            (current.score > prev.score) ? current : prev
          )
          setBestAttempt(best)
        }
      }
    } catch (err) {
      console.error('Error:', err)
      setError('Error al cargar los datos del examen')
    } finally {
      setIsLoading(false)
    }
  }

  const startExam = () => {
    if (!exam) return
    setExamStarted(true)
    setTimeRemaining(exam.time_limit * 60) // Convertir minutos a segundos
    setCurrentQuestionIndex(0)
    setAnswers({})
  }

  const selectAnswer = (questionId: string, answer: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }))
  }

  const goToQuestion = (index: number) => {
    setCurrentQuestionIndex(index)
    setVisitedQuestions(prev => new Set(prev).add(index))
  }

  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      goToQuestion(currentQuestionIndex + 1)
    }
  }

  const prevQuestion = () => {
    if (currentQuestionIndex > 0) {
      goToQuestion(currentQuestionIndex - 1)
    }
  }

  const finishExam = async () => {
    if (!exam || !profile) return

    setIsSaving(true)

    // Calcular puntaje
    let correct = 0
    let totalPoints = 0
    let earnedPoints = 0

    questions.forEach(q => {
      totalPoints += q.points
      if (answers[q.id] === q.correct_answer) {
        correct++
        earnedPoints += q.points
      }
    })

    const percentage = totalPoints > 0 ? Math.round((earnedPoints / totalPoints) * 100) : 0
    const hasPassed = percentage >= exam.passing_score

    setCorrectAnswers(correct)
    setScore(percentage)
    setPassed(hasPassed)

    // Guardar intento en la base de datos
    const attemptNumber = previousAttempts.length + 1
    const timeTaken = (exam.time_limit * 60) - timeRemaining

    try {
      // Intentar insertar el nuevo intento
      const { error } = await (supabase
        .from('exam_attempts') as any)
        .insert({
          student_id: student!.id,
          exam_id: exam.id,
          score: percentage,
          passed: hasPassed,
          answers: answers,
          time_taken_seconds: timeTaken,
          attempt_number: attemptNumber,
          completed_at: new Date().toISOString()
        })

      if (error) {
        console.error('Error guardando intento:', error)
        // Si es error de conflicto, intentar actualizar
        if (error.code === '23505' || error.message?.includes('duplicate') || error.code === 'PGRST116') {
          console.log('Intento ya existe, actualizando...')
        }
      }
    } catch (err) {
      console.error('Error guardando intento:', err)
    }

    // Disparar trigger de flujo
    if (user && unitId) {
      if (hasPassed) {
        triggerUnitExamPassed(user.id, unitId)
      } else {
        triggerUnitExamFailed(user.id, unitId)
      }
    }

    // Redirigir a la unidad con el resultado
    const resultState = {
      examResult: {
        passed: hasPassed,
        score: percentage,
        justFinished: true
      }
    }

    navigate(`/unit/${unitId}`, {
      state: resultState,
      replace: true
    })
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const restartExam = () => {
    setExamStarted(false)
    setExamFinished(false)
    setCurrentQuestionIndex(0)
    setAnswers({})
    setScore(0)
    setPassed(false)
    setCorrectAnswers(0)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    )
  }

  if (lessonsIncomplete) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <Link
            to={`/unit/${unitId}`}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver a la Unidad
          </Link>

          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-orange-500" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Debes completar todas las lecciones
            </h1>
            <p className="text-gray-600 mb-4">
              Has completado {completedLessonsCount} de {totalLessonsCount} lecciones. Termina todas las lecciones de esta unidad antes de rendir el examen.
            </p>
            <Link
              to={`/unit/${unitId}`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Ir a las lecciones
            </Link>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <Link
            to={`/unit/${unitId}`}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver a la Unidad
          </Link>

          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-yellow-500" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Examen no disponible
            </h1>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  // Pantalla de resultados
  if (examFinished) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-2xl mx-auto">
          <div className={`rounded-2xl shadow-sm p-8 text-center ${
            passed ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'
          }`}>
            <div className={`w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center ${
              passed ? 'bg-green-100' : 'bg-red-100'
            }`}>
              {passed ? (
                <Trophy className="w-10 h-10 text-green-600" />
              ) : (
                <XCircle className="w-10 h-10 text-red-600" />
              )}
            </div>

            <h1 className={`text-3xl font-bold mb-2 ${passed ? 'text-green-800' : 'text-red-800'}`}>
              {passed ? '¡Felicitaciones!' : 'No aprobaste'}
            </h1>

            <p className={`text-lg mb-6 ${passed ? 'text-green-700' : 'text-red-700'}`}>
              {passed
                ? 'Has aprobado el examen de esta unidad'
                : 'No alcanzaste el puntaje mínimo requerido'}
            </p>

            <div className="bg-white rounded-xl p-6 mb-6">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-3xl font-bold text-gray-900">{score}%</p>
                  <p className="text-sm text-gray-500">Tu puntaje</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-gray-900">{exam?.passing_score}%</p>
                  <p className="text-sm text-gray-500">Para aprobar</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-gray-900">{correctAnswers}/{questions.length}</p>
                  <p className="text-sm text-gray-500">Respuestas correctas</p>
                </div>
              </div>
            </div>

            <div className="flex gap-4 justify-center">
              <Link
                to={`/unit/${unitId}`}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Volver a la Unidad
              </Link>
              {!passed && (
                <button
                  onClick={restartExam}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  <RotateCcw className="w-5 h-5" />
                  Intentar de nuevo
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Calcular si puede reintentar
  const canRetry = exam ? (
    previousAttempts.length < exam.max_attempts &&
    (!bestAttempt || !bestAttempt.passed)
  ) : false

  const hasPassedBefore = bestAttempt?.passed || false

  // Pantalla de inicio del examen (o resultado si ya lo rindió)
  if (!examStarted) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-2xl mx-auto">
          <Link
            to={`/unit/${unitId}`}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver a la Unidad
          </Link>

          {/* Si ya aprobó el examen, mostrar resultado */}
          {hasPassedBefore && bestAttempt && (
            <div className="bg-green-50 border-2 border-green-200 rounded-2xl shadow-sm p-8 mb-6">
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-green-100 mx-auto mb-4 flex items-center justify-center">
                  <Trophy className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-green-800 mb-2">
                  ¡Examen Aprobado!
                </h2>
                <p className="text-green-700 mb-4">
                  Ya completaste este examen exitosamente
                </p>
                <div className="bg-white rounded-xl p-4 inline-block">
                  <div className="grid grid-cols-2 gap-6 text-center">
                    <div>
                      <p className="text-3xl font-bold text-green-600">{bestAttempt.score}%</p>
                      <p className="text-sm text-gray-500">Tu puntaje</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-gray-600">
                        {new Date(bestAttempt.completed_at).toLocaleDateString('es-ES', {
                          day: 'numeric',
                          month: 'short'
                        })}
                      </p>
                      <p className="text-sm text-gray-500">Fecha</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Si tiene intentos pero no aprobó */}
          {previousAttempts.length > 0 && !hasPassedBefore && bestAttempt && (
            <div className="bg-orange-50 border-2 border-orange-200 rounded-2xl shadow-sm p-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <RotateCcw className="w-6 h-6 text-orange-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-orange-800 mb-1">Intento anterior</h3>
                  <p className="text-orange-700 text-sm mb-2">
                    No alcanzaste el puntaje mínimo en tu último intento
                  </p>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="font-semibold text-orange-900">
                      Puntaje: {bestAttempt.score}%
                    </span>
                    <span className="text-orange-600">
                      Intentos: {previousAttempts.length}/{exam?.max_attempts}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6 text-white">
              <h1 className="text-2xl font-bold">{exam?.title}</h1>
              <p className="text-orange-100 mt-1">
                {unit && `Unidad ${unit.order_num}: ${unit.title}`}
              </p>
            </div>

            <div className="p-6">
              {exam?.description && (
                <p className="text-gray-600 mb-6">{exam.description}</p>
              )}

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <Clock className="w-6 h-6 text-blue-500" />
                    <div>
                      <p className="font-semibold text-gray-900">{exam?.time_limit} minutos</p>
                      <p className="text-sm text-gray-500">Tiempo limite</p>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500" />
                    <div>
                      <p className="font-semibold text-gray-900">{exam?.passing_score}%</p>
                      <p className="text-sm text-gray-500">Para aprobar</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mostrar solo si puede rendir el examen */}
              {!hasPassedBefore && canRetry && (
                <>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                      <div className="text-sm text-yellow-800">
                        <p className="font-medium mb-1">Antes de comenzar:</p>
                        <ul className="list-disc list-inside space-y-1">
                          <li>Asegúrate de tener una conexión estable a internet</li>
                          <li>El examen tiene {questions.length} preguntas</li>
                          <li>Una vez iniciado, el tiempo no se puede pausar</li>
                          <li>Puedes navegar entre preguntas antes de finalizar</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={startExam}
                    className="w-full py-4 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-semibold text-lg"
                  >
                    {previousAttempts.length > 0 ? 'Reintentar Examen' : 'Comenzar Examen'}
                  </button>
                </>
              )}

              {/* Si ya aprobó, no mostrar botón de comenzar */}
              {hasPassedBefore && (
                <div className="text-center text-gray-500 py-4">
                  Ya aprobaste este examen. ¡Continúa con la siguiente unidad!
                </div>
              )}

              {/* Si agotó los intentos sin aprobar */}
              {!hasPassedBefore && !canRetry && previousAttempts.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <XCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                  <p className="text-red-800 font-medium">
                    Has agotado todos tus intentos ({exam?.max_attempts})
                  </p>
                  <p className="text-red-600 text-sm mt-1">
                    Contacta a tu profesor para más información
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Historial de intentos */}
          {previousAttempts.length > 0 && (
            <div className="mt-6 bg-white rounded-2xl shadow-sm p-6">
              <h3 className="font-bold text-gray-900 mb-4">Historial de intentos</h3>
              <div className="space-y-3">
                {previousAttempts.map((attempt, index) => (
                  <div
                    key={attempt.id}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      attempt.passed ? 'bg-green-50' : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        attempt.passed ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-600'
                      }`}>
                        {previousAttempts.length - index}
                      </span>
                      <div>
                        <p className="font-medium text-gray-900">
                          {attempt.passed ? 'Aprobado' : 'No aprobado'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(attempt.completed_at).toLocaleDateString('es-ES', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-bold ${
                        attempt.passed ? 'text-green-600' : 'text-gray-600'
                      }`}>
                        {attempt.score}%
                      </p>
                      <p className="text-xs text-gray-500">
                        {Math.floor(attempt.time_taken_seconds / 60)}:{(attempt.time_taken_seconds % 60).toString().padStart(2, '0')} min
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  // Pantalla del examen en progreso
  const currentQuestion = questions[currentQuestionIndex]
  const answeredCount = Object.keys(answers).length
  const unansweredCount = questions.length - answeredCount

  return (
    <div className="min-h-screen bg-gray-50" translate="no" lang="fr">
      {/* Header con timer */}
      <div className="bg-white border-b sticky top-0 z-10 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-semibold text-gray-900">{exam?.title}</h1>
              <p className="text-sm text-gray-500">
                Pregunta {currentQuestionIndex + 1} de {questions.length}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 text-xs text-gray-400">
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" /> Respondida</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-400 inline-block" /> Sin responder</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-gray-300 inline-block" /> No visitada</span>
              </div>
              <div className={`flex items-center gap-2 px-4 py-2 rounded-xl font-mono text-lg font-bold ${
                timeRemaining <= 60
                  ? 'bg-red-100 text-red-600 animate-pulse'
                  : timeRemaining <= 300
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-blue-100 text-blue-600'
              }`}>
                <Clock className="w-5 h-5" />
                {formatTime(timeRemaining)}
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-orange-400 to-orange-600 transition-all duration-500"
              style={{ width: `${(answeredCount / questions.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Layout 2 columnas: Pregunta izq + Navegación der */}
      <div className="max-w-6xl mx-auto p-4 md:p-6">
        <div className="flex flex-col lg:flex-row gap-6">

          {/* Columna izquierda: Pregunta y opciones */}
          <div className="flex-1 min-w-0">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Pregunta header */}
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 px-6 py-4 border-b border-orange-100">
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-2 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold">
                    Pregunta {currentQuestionIndex + 1}
                  </span>
                  <span className="text-sm font-medium text-orange-600">
                    {currentQuestion?.points} pts
                  </span>
                </div>
              </div>

              {/* Pregunta body */}
              <div className="p-6">
                <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-6 leading-relaxed">
                  {currentQuestion?.question}
                </h2>

                {/* Audio player */}
                {currentQuestion?.audio_url && (
                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                        <Volume2 className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-blue-900">Escucha el audio</p>
                        <p className="text-xs text-blue-600">Puedes reproducirlo varias veces</p>
                      </div>
                    </div>
                    <AudioPlayer src={currentQuestion.audio_url} className="w-full" />
                  </div>
                )}

                {/* Opciones */}
                <div className="space-y-3">
                  {currentQuestion?.options?.map((option, index) => {
                    const isSelected = answers[currentQuestion.id] === option
                    const letter = String.fromCharCode(65 + index)

                    return (
                      <button
                        key={index}
                        onClick={() => selectAnswer(currentQuestion.id, option)}
                        className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                          isSelected
                            ? 'border-orange-500 bg-orange-50 shadow-md shadow-orange-100'
                            : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50/30'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                            isSelected
                              ? 'bg-orange-500 text-white scale-110'
                              : 'bg-gray-100 text-gray-500'
                          }`}>
                            {letter}
                          </span>
                          <span className={`flex-1 font-medium ${isSelected ? 'text-orange-900' : 'text-gray-700'}`}>
                            {option}
                          </span>
                          {isSelected && (
                            <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                          )}
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Footer navegación */}
              <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-between">
                <button
                  onClick={prevQuestion}
                  disabled={currentQuestionIndex === 0}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  <ChevronLeft className="w-5 h-5" />
                  Anterior
                </button>

                {currentQuestionIndex < questions.length - 1 ? (
                  <button
                    onClick={nextQuestion}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
                  >
                    Siguiente
                    <ChevronRight className="w-5 h-5" />
                  </button>
                ) : (
                  <button
                    onClick={() => setShowConfirmSubmit(true)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                  >
                    Finalizar examen
                    <CheckCircle className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Columna derecha: Panel de navegación */}
          <div className="w-full lg:w-72 flex-shrink-0">
            <div className="lg:sticky lg:top-24 space-y-4">
              {/* Mapa de preguntas */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-5 py-3.5 bg-gradient-to-r from-gray-50 to-slate-50 border-b">
                  <h3 className="font-semibold text-gray-800 text-sm">Preguntas</h3>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-5 gap-2">
                    {questions.map((q, index) => {
                      const isAnswered = !!answers[q.id]
                      const isVisited = visitedQuestions.has(index)
                      const isCurrent = index === currentQuestionIndex

                      let colorClass = 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                      if (isCurrent) {
                        colorClass = 'bg-orange-500 text-white ring-2 ring-orange-300 ring-offset-1'
                      } else if (isAnswered) {
                        colorClass = 'bg-green-500 text-white hover:bg-green-600'
                      } else if (isVisited) {
                        colorClass = 'bg-red-400 text-white hover:bg-red-500'
                      }

                      return (
                        <button
                          key={index}
                          onClick={() => goToQuestion(index)}
                          className={`w-full aspect-square rounded-lg flex items-center justify-center text-sm font-bold transition-all duration-200 ${colorClass}`}
                        >
                          {index + 1}
                        </button>
                      )
                    })}
                  </div>

                  {/* Leyenda */}
                  <div className="mt-4 pt-3 border-t border-gray-100 space-y-1.5">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span className="w-3 h-3 rounded bg-green-500 flex-shrink-0" />
                      Respondida ({answeredCount})
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span className="w-3 h-3 rounded bg-red-400 flex-shrink-0" />
                      Sin responder ({visitedQuestions.size - answeredCount > 0 ? visitedQuestions.size - answeredCount : 0})
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span className="w-3 h-3 rounded bg-gray-200 flex-shrink-0" />
                      No visitada ({questions.length - visitedQuestions.size})
                    </div>
                  </div>
                </div>
              </div>

              {/* Resumen + Botón enviar */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <div className="space-y-3 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Respondidas</span>
                    <span className="font-bold text-gray-900">{answeredCount}/{questions.length}</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        answeredCount === questions.length
                          ? 'bg-gradient-to-r from-green-400 to-green-600'
                          : 'bg-gradient-to-r from-orange-400 to-orange-600'
                      }`}
                      style={{ width: `${(answeredCount / questions.length) * 100}%` }}
                    />
                  </div>
                </div>

                <button
                  onClick={() => setShowConfirmSubmit(true)}
                  disabled={answeredCount === 0}
                  className={`w-full py-3 rounded-xl font-semibold text-sm transition-all ${
                    answeredCount === questions.length
                      ? 'bg-green-600 text-white hover:bg-green-700 shadow-lg shadow-green-200'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed'
                  }`}
                >
                  {answeredCount === questions.length
                    ? 'Enviar respuestas'
                    : `Faltan ${unansweredCount} respuestas`}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de confirmación */}
      {showConfirmSubmit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className={`p-6 text-center ${
              unansweredCount > 0
                ? 'bg-gradient-to-b from-yellow-50 to-white'
                : 'bg-gradient-to-b from-green-50 to-white'
            }`}>
              <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${
                unansweredCount > 0 ? 'bg-yellow-100' : 'bg-green-100'
              }`}>
                {unansweredCount > 0 ? (
                  <AlertTriangle className="w-8 h-8 text-yellow-600" />
                ) : (
                  <CheckCircle className="w-8 h-8 text-green-600" />
                )}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {unansweredCount > 0
                  ? '¿Enviar con respuestas pendientes?'
                  : '¿Listo para enviar?'}
              </h3>
              <p className="text-gray-600 mb-1">
                {unansweredCount > 0
                  ? `Tienes ${unansweredCount} pregunta${unansweredCount > 1 ? 's' : ''} sin responder.`
                  : 'Has respondido todas las preguntas.'}
              </p>
              <p className="text-sm text-gray-500">
                {unansweredCount > 0
                  ? 'Las preguntas sin responder se contarán como incorrectas.'
                  : 'Una vez enviado no podrás modificar tus respuestas.'}
              </p>
            </div>

            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => setShowConfirmSubmit(false)}
                className="flex-1 py-3 px-4 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
              >
                Volver al examen
              </button>
              <button
                onClick={() => {
                  setShowConfirmSubmit(false)
                  finishExam()
                }}
                className="flex-1 py-3 px-4 rounded-xl bg-green-600 text-white font-semibold hover:bg-green-700 transition-colors shadow-lg shadow-green-200"
              >
                Enviar respuestas
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
